import { Controller } from "@hotwired/stimulus"

// dropdown-filter — shared popover filter control for V2 list-page toolbars.
//
// Markup (see app/views/shared/_dropdown_filter.html.erb):
//   <div data-controller="dropdown-filter">
//     <button data-action="dropdown-filter#toggle"
//             data-dropdown-filter-target="trigger">Label</button>
//     <div data-dropdown-filter-target="menu" hidden>
//       <button data-action="dropdown-filter#select"
//               data-params='{"customer_id":"…"}'>Option</button>
//     </div>
//   </div>
//
// Each option carries a `data-params` JSON object of query-param changes.
// Selecting an option merges those params into the current URL and
// navigates. A null / empty value removes the param. The `page` param is
// always cleared so a new filter returns to the first page of results.
export default class extends Controller {
  static targets = ["menu", "trigger"]

  connect() {
    this._onDocClick = this._onDocClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
  }

  disconnect() {
    this._stopListening()
  }

  toggle(event) {
    event.preventDefault()
    if (this.menuTarget.hidden) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this._closeOthers()
    this.menuTarget.hidden = false
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
    }
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKeydown)
  }

  close() {
    if (this.menuTarget.hidden) return
    this.menuTarget.hidden = true
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }
    this._stopListening()
  }

  // Merge the chosen option's params into the current URL and navigate.
  select(event) {
    event.preventDefault()

    let changes
    try {
      changes = JSON.parse(event.currentTarget.dataset.params || "{}")
    } catch (_error) {
      return
    }

    const url = new URL(window.location.href)
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        url.searchParams.delete(key)
      } else {
        url.searchParams.set(key, value)
      }
    })
    url.searchParams.delete("page")

    const destination = url.toString()
    if (window.Turbo) {
      window.Turbo.visit(destination)
    } else {
      window.location.assign(destination)
    }
  }

  // Only one filter popover should be open at a time.
  _closeOthers() {
    document
      .querySelectorAll('[data-controller~="dropdown-filter"]')
      .forEach((element) => {
        if (element === this.element) return
        const menu = element.querySelector('[data-dropdown-filter-target="menu"]')
        const trigger = element.querySelector('[data-dropdown-filter-target="trigger"]')
        if (menu) menu.hidden = true
        if (trigger) trigger.setAttribute("aria-expanded", "false")
      })
  }

  _onDocClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  _onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      if (this.hasTriggerTarget) this.triggerTarget.focus()
    }
  }

  _stopListening() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKeydown)
  }
}
